import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { RolesFilters } from "./useRolesSubPage";

export interface RolesFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: RolesFilters;
  onApply: (filters: RolesFilters) => void;
}

const parseAssignment = (value: string): RolesFilters["assignment"] =>
  value === "assigned" ? "assigned" : value === "unassigned" ? "unassigned" : undefined;

const parsePermissions = (value: string): RolesFilters["permissions"] =>
  value === "with" ? "with" : value === "without" ? "without" : undefined;

export const RolesFilterDialog = ({ open, onOpenChange, filters, onApply }: RolesFilterDialogProps) => {
  const { t } = useTranslation("settings");

  const [stagedFilters, setStagedFilters] = useState<RolesFilters>(filters);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setStagedFilters(filters);
  }

  const hasStagedFilters = !!stagedFilters.assignment || !!stagedFilters.permissions;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent divided>
        <div className="space-y-1 p-4">
          <DialogTitle>{t("Filter Roles")}</DialogTitle>
          <DialogDescription className="text-xs">{t("Narrow down the roles shown below.")}</DialogDescription>
        </div>
        <div className="space-y-4 p-4">
          <div className="space-y-1.5">
            <span className="text-sm font-medium">{t("Team members")}</span>
            <Select
              value={stagedFilters.assignment ?? "all"}
              onValueChange={(value) => setStagedFilters({ ...stagedFilters, assignment: parseAssignment(value) })}
            >
              <Select.Trigger className="w-full">
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                <Select.Item value="all">{t("Any number of members")}</Select.Item>
                <Select.Item value="assigned">{t("Has assigned members")}</Select.Item>
                <Select.Item value="unassigned">{t("No assigned members")}</Select.Item>
              </Select.Content>
            </Select>
          </div>
          <div className="space-y-1.5">
            <span className="text-sm font-medium">{t("Permissions")}</span>
            <Select
              value={stagedFilters.permissions ?? "all"}
              onValueChange={(value) => setStagedFilters({ ...stagedFilters, permissions: parsePermissions(value) })}
            >
              <Select.Trigger className="w-full">
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                <Select.Item value="all">{t("Any permissions")}</Select.Item>
                <Select.Item value="with">{t("Grants permissions")}</Select.Item>
                <Select.Item value="without">{t("No permissions")}</Select.Item>
              </Select.Content>
            </Select>
          </div>
        </div>
        <DialogFooter className="bg-gray-100 rounded-b-md">
          <Button
            variant="outline"
            disabled={!hasStagedFilters}
            onClick={() => setStagedFilters({})}
            data-trackingid="roles-filter-dialog-clear"
          >
            {t("Clear")}
          </Button>
          <Button onClick={() => onApply(stagedFilters)} data-trackingid="roles-filter-dialog-apply">
            {t("Apply")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
